import { replaceVerbatim } from "@project/shared/src/replaceVerbatim";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";

/**
 * Where the dither function is spliced in: the fragment shader's last
 * declaration before `main`. Three's `OutputShader` has carried this line
 * unchanged for many releases, but it is still their string and not ours.
 */
const DECLARATION_ANCHOR = "varying vec2 vUv;";

/** The shader's final closing brace, i.e. the end of `main`. */
const END_OF_MAIN = /\}\s*$/;

/**
 * Hash of the fragment coordinate. Value noise is enough here — the eye is
 * looking for the *absence* of a contour, not for a well-distributed pattern,
 * and this is one `sin` per pixel on a pass that is already reading a texture.
 */
const DITHER_FUNCTION = /* glsl */ `
float productViewerDitherNoise(vec2 fragment) {
  return fract(sin(dot(fragment, vec2(12.9898, 78.233))) * 43758.5453);
}
`;

/**
 * Half an output LSB of noise, signed differently per channel so the pattern
 * breaks the contour without tinting anything. Same shape as three's own
 * `dithering_fragment` chunk, which cannot be included here: `OutputPass` is a
 * `RawShaderMaterial`, so it gets none of the renderer's shader prefix and the
 * chunk's `rand()` would be undefined.
 */
const DITHER_APPLICATION = /* glsl */ `
  vec3 ditherShift = vec3(0.25, -0.25, 0.25) / 255.0;
  ditherShift = mix(
    2.0 * ditherShift,
    -2.0 * ditherShift,
    productViewerDitherNoise(gl_FragCoord.xy)
  );
  gl_FragColor.rgb += ditherShift;
`;

/**
 * Three's `OutputPass` with dithering added.
 *
 * The composer works in `HalfFloatType` and this pass is where the frame is
 * quantised to the canvas' 8 bits. Everything the product stage renders is the
 * worst case for that: one HDRI as the only light source, `NeutralToneMapping`
 * rolling the highlights off gently, a large smooth backdrop and a blurred
 * `ContactShadows` pool. Those are wide, low-gradient areas where neighbouring
 * pixels round to the same byte for a long stretch and then step, which reads
 * as concentric rings around the case and stair-steps across the shadow
 * falloff. Sub-LSB noise decorrelates the rounding so the gradient dissolves
 * into imperceptible pixel noise instead.
 *
 * Applied after the colour-space transfer, matching where three puts
 * `dithering_fragment` in its own material shaders: it has to be the last thing
 * before the write, in the same space the quantisation happens in.
 */
export class ProductViewerOutputPass extends OutputPass {
  constructor() {
    super();

    const shader = this.material.fragmentShader;
    // Ship an undithered frame rather than a broken one if three ever
    // restructures the shader out from under these two anchors.
    if (!shader.includes(DECLARATION_ANCHOR) || !END_OF_MAIN.test(shader)) {
      return;
    }

    const declared = replaceVerbatim(
      shader,
      DECLARATION_ANCHOR,
      `${DECLARATION_ANCHOR}\n${DITHER_FUNCTION}`,
    );
    this.material.fragmentShader = replaceVerbatim(
      declared,
      END_OF_MAIN,
      `${DITHER_APPLICATION}\n}`,
    );
  }
}
